import React, { useState, useRef, useEffect } from 'react'
import { useChatStore } from '../lib/store'
import { LLMService } from '../services/llm/llm-service'
import { LLMProvider } from '../types/llm'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import TypingIndicator from './TypingIndicator'

const ChatInterface: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  const { 
    currentSessionId, 
    sessions, 
    selectedProvider, 
    selectedModel,
    apiKeys,
    customProviderConfig,
    addMessage,
    updateMessage 
  } = useChatStore()

  const currentSession = sessions.find(s => s.id === currentSessionId)
  const messages = currentSession?.messages || []

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = async (content: string) => {
    if (!selectedProvider || !selectedModel || !apiKeys[selectedProvider]) {
      setError('Please configure your LLM provider and API key')
      return
    }

    // Add user message
    addMessage({
      role: 'user',
      content,
      provider: selectedProvider,
      model: selectedModel
    })

    setIsLoading(true)
    setError(null)

    try {
      const llmConfig = {
        provider: selectedProvider,
        apiKey: apiKeys[selectedProvider],
        model: selectedModel,
        ...(selectedProvider === LLMProvider.Custom && { baseUrl: customProviderConfig.baseUrl })
      }
      
      const llmService = new LLMService(llmConfig)

      // Create assistant message placeholder
      const assistantMessageId = addMessage({
        role: 'assistant',
        content: '',
        provider: selectedProvider,
        model: selectedModel
      })

      // Get the response (non-streaming for now to fix Venice)
      const chatMessages = [...messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp
      })), {
        id: crypto.randomUUID(),
        role: 'user' as const,
        content,
        timestamp: new Date()
      }]
      
      const response = await llmService.chat(chatMessages)
      console.log('Response received:', response)
      console.log('Updating message ID:', assistantMessageId)
      updateMessage(assistantMessageId, response)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      addMessage({
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request.',
        error: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!currentSessionId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Welcome to Aniify
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Configure your LLM provider to start chatting
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <MessageList messages={messages} />
        {isLoading && <TypingIndicator />}
        {error && (
          <div className="mx-auto max-w-3xl mt-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <MessageInput 
        onSendMessage={handleSendMessage}
        disabled={isLoading}
      />
    </div>
  )
}

export default ChatInterface
